import React, { useEffect, useRef, useState } from "react";
import createContext from "pex-context";
import { perspective as createCamera } from "pex-cam";
import createDrawGridCmd from "./commands/grid";
import createDrawInstancedPathsCmd from "./commands/instanced-paths";
import createDrawTrunkCmd from "./commands/trunk";
import createDrawIconsCmd from "./commands/icons";
import { generateRings, animateRings } from "./generate-rings";
import generateTrunk from "./generate-trunk";
import generateBark from "./generate-bark";
import generateAxis from "./generate-axis";
import generateAxisIcons from "./generate-axis-icons";
import { quadInOut } from "eases";
import { useTweaks } from "use-tweaks";
import { utils, vec3 } from "pex-math";
import useSize from "./utils/use-size";
import Mesh from "./utils/mesh";
import "./visualisation.scss";
import { loadImage } from "pex-io";
import { fromHex } from "pex-color";
import Stages from "./config-stages";

const useDebouncedEffect = (effect, deps, delay, animationTime) => {
  useEffect(() => {
    let handler = 0;
    if (animationTime < 1 || deps[1]?.finished) {
      effect();
    } else {
      handler = setTimeout(() => effect(), delay);
    }

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), delay]);
};

const { map, clamp } = utils;

const AnimationDuration = 22;
const pixelRatio = 1.5;

const CameraKeyframes = [
  {
    position: [0, -3.2658290618311305, 2.463055353632299],
    target: [0, 0.4214285758885947, -0.571596698328682],
  },
  {
    position: [0, -1.0368520580775542, 17.932221958163986],
    target: [0, 0.4214285758885947, -0.571596698328682],
  },
];

const MobleCameraKeyframes = [
  {
    position: [0, -3.2658290618311305, 2.463055353632299],
    target: [0, 0.4214285758885947, -0.571596698328682],
  },
  {
    position: [0, -1.0368520580775542, 12.932221958163986],
    target: [0, 0.4214285758885947, -0.571596698328682],
  },
];

function loadTexture(ctx, url, texture, mipmap) {
  if (!texture) {
    texture = ctx.texture2D({});
  }
  texture.src = url;
  loadImage(url).then((img) => {
    ctx.update(texture, {
      data: img,
      flipY: true,
      mipmap: mipmap,
      min: mipmap ? ctx.Filter.LinearMipmapLinear : ctx.Filter.Linear,
      wrapS: ctx.Wrap.Repeat,
      wrapT: ctx.Wrap.Repeat,
      aniso: 16,
    });
  });
  return texture;
}

function init({ canvas }) {
  const ctx = createContext({
    canvas: canvas,
    alpha: true,
    antialias: true,
    preserveDrawingBuffer: true,
    powerPreference: "high-performance",
  });
  // ctx.gl.getExtension("EXT_shader_texture_lod");
  // ctx.gl.getExtension("OES_standard_derivatives");d
  // ctx.gl.getExtension("WEBGL_draw_buffers");
  // ctx.gl.getExtension("OES_texture_float");
  // ctx.gl.getExtension("EXT_color_buffer_half_float");
  // ctx.gl.getExtension("WEBGL_color_buffer_float");

  const clearCmd = {
    pass: ctx.pass({
      clearColor: [0, 0, 0, 0],
      clearDepth: 1,
    }),
  };
  const camera = createCamera({
    position: [0, 0, 15],
    fov: Math.PI / 3, // 60 deg,
    near: 0.1,
    far: 100,
  });
  const drawGridCmd = createDrawGridCmd(ctx);
  const drawInstancedPathsCmd = createDrawInstancedPathsCmd(ctx);

  const drawTrunkCmd = createDrawTrunkCmd(ctx);
  const drawIconsCmd = createDrawIconsCmd(ctx);

  return {
    ctx,
    clearCmd,
    camera,
    drawGridCmd,
    drawInstancedPathsCmd,

    drawTrunkCmd,
    drawIconsCmd,
  };
}

function initDynamicResources({ ctx, data, state }) {
  // console.log("viz.initDynamicResources");

  const baseColor = fromHex(data.color);
  const baseBarkColor = [
    baseColor[0] * 0.75,
    baseColor[1] * 0.75,
    baseColor[2] * 0.75,
    1,
  ];

  const [rings, normalizationFactor] = generateRings({ data, subdivide: true });
  const ringsGeom = animateRings({ data, rings, reveal: 0, baseColor });
  const ringsMesh = state.ringsMesh || new Mesh(ctx);
  ringsMesh.position = [0, 0, 0.2];
  ringsMesh.positions = ringsGeom.positions;
  ringsMesh.vertexColors = ringsGeom.vertexColors;

  const trunkGeom = generateTrunk(rings[rings.length - 1]);
  const trunkMesh = state.trunkMesh || new Mesh(ctx);
  trunkMesh.positions = trunkGeom.positions;
  trunkMesh.indices = trunkGeom.cells;

  const barkGeom = generateBark(rings[rings.length - 1], {
    ...data.bark,
    seed: data.id,
  });
  const barkMesh = state.barkMesh || new Mesh(ctx);
  barkMesh.positions = barkGeom.positions;
  barkMesh.indices = barkGeom.cells;

  const axisGeom = generateAxis({
    mobile: state.mobile,
    lastRing: data.rings[data.rings.length - 1],
    normalizationFactor,
  });
  const axisMesh = state.axisMesh || new Mesh(ctx);
  axisMesh.position = [0, 0, -0.1];
  axisMesh.positions = axisGeom.positions;
  axisMesh.vertexColors = axisGeom.vertexColors;

  const iconsGeom = generateAxisIcons({
    mobile: state.mobile,
    lastRing: data.rings[data.rings.length - 1],
    normalizationFactor,
  });
  const iconsMesh = state.iconsMesh || new Mesh(ctx);
  iconsMesh.position = [0, 0, -0.1];
  iconsMesh.positions = iconsGeom.positions;
  iconsMesh.uvs = iconsGeom.uvs;
  iconsMesh.offsets = iconsGeom.offsets;
  iconsMesh.instanceUvs = iconsGeom.instanceUvs;
  iconsMesh.indices = iconsGeom.cells;
  iconsMesh.instances = iconsGeom.offsets.length;

  const noiseTexture = loadTexture(
    ctx,
    "/textures/perlin-noise.png",
    state.noiseTexture
  );
  const axisIconsTexture = loadTexture(
    ctx,
    "/textures/icons/axis-icons2.png",
    state.axisIconsTexture,
    true
  );
  const woodTexture = loadTexture(ctx, data.woodTexture, state.woodTexture);
  const barkTexture = loadTexture(ctx, data.barkTexture, state.barkTexture);
  const dirtTexture = loadTexture(ctx, data.dirtTexture, state.dirtTexture);
  const barkDirtTexture = loadTexture(
    ctx,
    data.barkDirtTexture,
    state.barkDirtTexture
  );

  return {
    rings,
    ringsMesh,
    trunkMesh,
    barkMesh,
    iconsMesh,
    axisIconsTexture,
    noiseTexture,
    woodTexture,
    barkTexture,
    dirtTexture,
    barkDirtTexture,
    axisMesh,
    baseColor,
    baseBarkColor,
    data: data,
  };
}

function render({ vizState, axisAlpha, ringsAlpha }) {
  let {
    ctx,
    clearCmd,
    camera,
    drawInstancedPathsCmd,
    drawTrunkCmd,
    drawIconsCmd,
    ringsMesh,
    axisMesh,
    trunkMesh,
    barkMesh,
    iconsMesh,
    noiseTexture,
    woodTexture,
    barkTexture,
    dirtTexture,
    barkDirtTexture,
    axisIconsTexture,
    trunkReveal,
    baseColor,
    baseBarkColor,
  } = vizState;

  const textures = [
    noiseTexture,
    woodTexture,
    dirtTexture,
    barkTexture,
    barkDirtTexture,
  ];

  ctx.submit(clearCmd);
  for (let texture of textures) {
    if (!texture.width) {
      // console.log('returning', texture.src, texture.width)
      return;
    }
  }
  // ctx.debug(true)
  //console.log('render');
  // drawGridCmd({ camera });

  drawInstancedPathsCmd({
    ctx,
    camera,
    mesh: ringsMesh,
    opacity: 1,
    cutout: true,
  });

  drawIconsCmd({
    ctx,
    camera,
    mesh: iconsMesh,
    axisIconsTexture: axisIconsTexture,
    opacity: axisAlpha,
  });

  drawTrunkCmd({
    ctx,
    camera,
    mesh: trunkMesh,
    noiseTexture,
    texture: woodTexture,
    dirtTexture: dirtTexture,
    reveal: trunkReveal,
    baseColor,
  });

  drawInstancedPathsCmd({
    ctx,
    camera,
    mesh: axisMesh,
    opacity: axisAlpha,
    colorAlphaThickness: 0,
  });

  drawTrunkCmd({
    ctx,
    camera,
    mesh: barkMesh,
    noiseTexture,
    texture: barkTexture,
    dirtTexture: barkDirtTexture,
    reveal: trunkReveal,
    baseColor: baseBarkColor,
  });

  drawInstancedPathsCmd({
    ctx,
    camera,
    mesh: ringsMesh,
    opacity: ringsAlpha,
    colorAlphaThickness: 1,
  });
}

function cleanup(vizState) {}

let requestId;

const Visualisation = (props) => {
  let ref = props.canvasRef;
  let containerRef = useRef();
  const { onStageChange, paused } = props;

  const [vizState, setVizState] = useState({
    sessionId: Date.now(),
    ctx: null,
    clearCmd: null,
    camera: null,
    grid: null,
    ringsGeo: null,
    currentStageName: null,
    baseColor: [1, 1, 1, 1],
    baseBarkColor: [1, 1, 1, 1],
    prevTime: Date.now(),
    animationTime: 0,
    mobile: false,
  });

  const { globalTime, animation, debugLayout } = useTweaks({
    globalTime: { value: 0, min: 0, max: AnimationDuration },
    debugLayout: false,
    animation: {
      value: "once",
      options: ["none", "once", "loop"],
    },
  });

  const size = useSize(containerRef);
  const canvasWidth = Math.max(1024, size.width * pixelRatio) | 0;
  const canvasHeight = Math.max(1024, size.height * pixelRatio) | 0;

  useDebouncedEffect(
    () => {
      let canvas = ref.current;
      let { data, finished } = props;
      //if (paused) return;

      if (finished) {
        vizState.animationTime = AnimationDuration;
      }

      if (requestId) {
        cancelAnimationFrame(requestId);
      }
      // console.log("viz useEffect", data);

      let latestState = vizState;
      let hasInitialised = false;

      if (!vizState.ctx) {
        hasInitialised = true;
        const staticState = init({ canvas });
        const dynamicState = initDynamicResources({
          ctx: staticState.ctx,
          data,
          state: {},
        });
        latestState = {
          ...vizState,
          ...staticState,
          ...dynamicState,
        };
        setVizState(latestState);
      } else {
        //this is not enough
        vizState.ctx.set({
          width: canvasWidth,
          height: canvasHeight,
        });
        vizState.ctx.defaultState.viewport[2] = canvasWidth;
        vizState.ctx.defaultState.viewport[3] = canvasHeight;
        vizState.ctx.defaultState.pass.framebuffer.width = canvasWidth;
        vizState.ctx.defaultState.pass.framebuffer.height = canvasHeight;
        vizState.ctx.gl.viewport(0, 0, canvasWidth, canvasHeight);
      }

      const raf = () => {
        const now = Date.now();
        let deltaTime = (now - latestState.prevTime) / 1000;
        if (deltaTime > 1) {
          deltaTime = 0;
        }
        latestState.prevTime = now;

        //const animationMode = animation.value || animation;
        const animationMode = paused ? "none" : "once";
        //console.log(globalTime, latestState);
        switch (animationMode) {
          case "none":
            /*  
          const gt =
              globalTime.value !== undefined ? globalTime.value : globalTime; //TODO: why do i need .value
             if (latestState.animationTime !== gt) {
              latestState.animationTime = gt;
            }
            */
            break;
          case "once":
            if (latestState.animationTime < AnimationDuration) {
              latestState.animationTime = Math.min(
                latestState.animationTime + deltaTime,
                AnimationDuration
              );
            }
            break;
          case "loop":
            latestState.animationTime = (Date.now() / 1000) % AnimationDuration;
            break;
          default:
        }
        const t = latestState.animationTime;

        const currentStage = [...Stages]
          .reverse()
          .find((stage) => stage.time <= t);

        if (
          currentStage &&
          currentStage.name !== latestState.currentStageName
        ) {
          latestState.currentStageName = currentStage.name;
          onStageChange({ stage: currentStage.name });
        }

        let linesReveal = quadInOut(clamp(map(t, 0, 8, 0, 1), 0, 1));
        let thicknessRandom = quadInOut(clamp(map(t, 8, 13, 0, 1), 0, 1));
        let thicknessReveal = quadInOut(clamp(map(t, 13, 15, 0, 1), 0, 1));
        let trunkReveal = quadInOut(clamp(map(t, 15, 20, 0, 1), 0, 1));
        let cameraZoomOut = quadInOut(clamp(map(t, 0, 8, 0, 1), 0, 1));
        let axisAlpha = quadInOut(clamp(map(t, 19, 22, 0, 1), 0, 1));

        if (
          props.data !== latestState.data ||
          props.reset !== latestState.reset ||
          props.mobile !== latestState.mobile
        ) {
          //console.log("viz new data -> resetting time");
          if (
            props.data !== latestState.data ||
            props.reset !== latestState.reset
          ) {
            latestState.animationTime = 0;
          }
          latestState.data = props.data;
          latestState.mobile = props.mobile;
          latestState.reset = props.reset;
          Object.assign(
            latestState,
            initDynamicResources({
              ctx: latestState.ctx,
              data,
              state: latestState,
            })
          );
          setVizState(latestState);
        }
        const ringsGeom = animateRings({
          data,
          rings: latestState.rings,
          reveal: linesReveal,
          thicknessReveal: thicknessReveal,
          thicknessRandom: thicknessRandom,
          baseColor: latestState.baseColor,
        });
        latestState.ringsMesh.positions = ringsGeom.positions;
        latestState.ringsMesh.vertexColors = ringsGeom.vertexColors;
        latestState.trunkReveal = trunkReveal;

        const keyframes = props.mobile ? MobleCameraKeyframes : CameraKeyframes;

        latestState.camera.set({
          //GC
          position: vec3.lerp(
            [...keyframes[0].position],
            keyframes[1].position,
            cameraZoomOut
          ),
          target: vec3.lerp(
            [...keyframes[0].target],
            keyframes[1].target,
            cameraZoomOut
          ),
        });

        render({
          vizState: latestState,
          axisAlpha,
          ringsAlpha: 1 - trunkReveal,
        });
        if (
          !paused ||
          latestState.currentStageName !== "done" ||
          latestState.animationTime === 0
        ) {
          requestId = requestAnimationFrame(raf);
        }
      };

      if (requestId) {
        cancelAnimationFrame(requestId);
      }
      // console.log("viz raf start");
      if (!hasInitialised) {
        raf();
      }

      return () => {
        cleanup(vizState);
        cancelAnimationFrame(requestId);
      };
    },
    [vizState, props, globalTime, animation, size, paused],
    300,
    vizState.animationTime
  );

  return (
    <div className="visualisation" ref={containerRef}>
      <canvas
        className={"visualisation-canvas " + (debugLayout.value ? "debug" : "")}
        ref={ref}
        width={canvasWidth}
        height={canvasHeight}
        style={{
          left: `${size.x | 0}px`,
          top: `${size.y | 0}px`,
          width: `${size.width | 0}px`,
          height: `${size.height | 0}px`,
          transform: `translate3d(0, 0, 0)`,
        }}
      />
    </div>
  );
};

export default Visualisation;
